// Runtime decode / streaming defaults bundled with the model preprocess JSON.
// Colleagues load one preprocess.json alongside the ONNX export; createAsr()
// exposes the parsed profile on `asr.decode`.

const NGRAM_FALLBACK = Object.freeze({
  order:     2,
  weight:    0.5,
  smoothing: 0.1,
});

const BEAM_FALLBACK = Object.freeze({
  transcribe:         { beamWidth: 8, topK: 50, lmAlpha: -3.0,  lmBeta: 1.0 },
  transcribePassage:  { beamWidth: 4, topK: 30, lmAlpha: -4.25, lmBeta: 1.0 },
  session:            { beamWidth: 4, topK: 30, lmAlpha: -3.0,  lmBeta: 1.0 },
  sessionPassage:     { beamWidth: 4, topK: 30, lmAlpha: -3.0,  lmBeta: 1.0 },
});

const STREAMING_FALLBACK = Object.freeze({
  rollingStrideMs: 200,
  micHighpassHz:   80,
  micHighpassQ:    0.7,
});

const CONSENSUS_FALLBACK = Object.freeze({
  scoreThreshold:      0.50,
  fastEmitThreshold:   0.85,
  interiorMinScore:    0.75,
  passageScoreBonus:   0.25,
  minSightings:        2,
  persistentSightings: 3,
  persistentThreshold: 0.10,
  staleLeftEdgeChunks: 1,
  staleLeftEdgeScore:  0.70,
  abandonMaxScore:     0.40,
});

function num(obj, key, fallback) {
  const v = obj?.[key];
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

function ngramProfile(raw) {
  const c = raw ?? {};
  const f = NGRAM_FALLBACK;
  return Object.freeze({
    order:     num(c, "lm_order",     f.order),
    weight:    num(c, "lm_weight",    f.weight),
    smoothing: num(c, "lm_smoothing", f.smoothing),
  });
}

function beamProfile(raw, fallback) {
  return Object.freeze({
    beamWidth: num(raw, "beam_width", fallback.beamWidth),
    topK:      num(raw, "top_k",      fallback.topK),
    lmAlpha:   num(raw, "lm_alpha",   fallback.lmAlpha),
    lmBeta:    num(raw, "lm_beta",    fallback.lmBeta),
  });
}

function consensusProfile(raw, fallback = CONSENSUS_FALLBACK) {
  const c = raw ?? {};
  const f = fallback;
  return Object.freeze({
    scoreThreshold:      num(c, "score_threshold",      f.scoreThreshold),
    fastEmitThreshold:   num(c, "fast_emit_threshold",  f.fastEmitThreshold),
    interiorMinScore:    num(c, "interior_min_score",   f.interiorMinScore),
    passageScoreBonus:   num(c, "passage_score_bonus",  f.passageScoreBonus),
    minSightings:        num(c, "min_sightings",        f.minSightings),
    persistentSightings: num(c, "persistent_sightings", f.persistentSightings),
    persistentThreshold: num(c, "persistent_threshold", f.persistentThreshold),
    staleLeftEdgeChunks: num(c, "stale_left_edge_chunks", f.staleLeftEdgeChunks),
    staleLeftEdgeScore:  num(c, "stale_left_edge_score",  f.staleLeftEdgeScore),
    abandonMaxScore:     num(c, "abandon_max_score",      f.abandonMaxScore),
  });
}

/**
 * @param {object} cfg preprocess.json (must include mel fields; may include `decode`)
 * @returns {Readonly<{
 *   snapThreshold: number,
 *   strideMs: number|null,
 *   transcribe: object,
 *   transcribePassage: object,
 *   session: object,
 *   sessionPassage: object,
 *   streaming: object,
 *   consensus: object,
 * }>}
 */
export function decodeProfileFromPreprocess(cfg) {
  const d = cfg?.decode ?? {};
  const strideMs = d.stride_ms === null || d.stride_ms === undefined
    ? null
    : num(d, "stride_ms", null);

  return Object.freeze({
    snapThreshold: num(d, "snap_threshold", 0.55),
    strideMs,
    ngram: ngramProfile(d.ngram),
    transcribe:        beamProfile(d.transcribe,        BEAM_FALLBACK.transcribe),
    transcribePassage: beamProfile(d.transcribe_passage, BEAM_FALLBACK.transcribePassage),
    session:           beamProfile(d.session,           BEAM_FALLBACK.session),
    sessionPassage:    beamProfile(
      d.session_passage ?? d.session,
      BEAM_FALLBACK.sessionPassage,
    ),
    streaming: Object.freeze({
      rollingStrideMs: num(d.streaming, "rolling_stride_ms", STREAMING_FALLBACK.rollingStrideMs),
      micHighpassHz:   num(d.streaming, "mic_highpass_hz",   STREAMING_FALLBACK.micHighpassHz),
      micHighpassQ:    num(d.streaming, "mic_highpass_q",    STREAMING_FALLBACK.micHighpassQ),
    }),
    consensus: consensusProfile(d.consensus),
  });
}

export {
  BEAM_FALLBACK,
  CONSENSUS_FALLBACK,
  NGRAM_FALLBACK,
  STREAMING_FALLBACK,
};
